import React from 'react';
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

import Layout from "./components/Layout";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import SocialAssets from "./pages/SocialAssets";
import DesignSystem from "./pages/DesignSystem";
import DevSystem from "./pages/DevSystem";
import OperatorAdmin from "./pages/OperatorAdmin";
import OperatorAdminSimple from "./pages/OperatorAdminSimple";
import OperatorOnboarding from "./pages/OperatorOnboarding";
import Directory from "./pages/Directory";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Signup from "./pages/Signup";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ComingSoon from "./pages/ComingSoon";
import PricingPage from "./pages/Pricing";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Contact from "./pages/Contact";
import ContactThankYou from "./pages/ContactThankYou";
import AdminLanding from "./pages/AdminLanding";
import ProjectIntro from "./pages/ProjectIntro";
import Sitemap from "./pages/Sitemap";
import ProtectedRoute from "./components/ProtectedRoute";
import { AdminRoute } from "./components/RoleBasedRoute";
import VerifyEmail from "./pages/VerifyEmail";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import AcceptableUsePolicy from "./pages/AcceptableUsePolicy";
import CookieConsent from "react-cookie-consent";
import BlogIndexPage from "./pages/admin/BlogIndexPage";
import BlogFormPage from "./pages/admin/BlogFormPage";
import ErrorPage from "./pages/ErrorPage";
import TermsOfUse from "./pages/TermsOfUse";
import MaintenancePage from "./pages/MaintenancePage";
import { AdminLayout } from './components/admin/AdminLayout'
import UsersPage from './pages/admin/UsersPage'
import OperatorsPage from './pages/admin/OperatorsPage';
import LocationsPage from './pages/admin/LocationsPage';
import LeadsPage from './pages/admin/LeadsPage';
import SaunaWizardPage from './pages/SaunaWizardPage';
import TempSignupPage from './pages/TempSignupPage';
import TempComingSoonLandingPage from './pages/TempComingSoonLandingPage';
import TempMarketingLandingPage from './pages/TempMarketingLandingPage';
import WhyKit from "./pages/WhyKit";
import WeNeedYourHelp from "./pages/WeNeedYourHelp";
import YourHelp from "./pages/YourHelp";
import HowItWorks from "./pages/HowItWorks";
import Landing from "./pages/Landing";
import SaunaDipHelpBook from "./components/SaunaDipHelpBook 2";
import ContentReviewPage from "./pages/ContentReview";
import ResetPassword from "./pages/ResetPassword";
import ChooseSignup from "./pages/ChooseSignup";
import SignupGuest from "./pages/SignupGuest";
import SignupOperator from "./pages/SignupOperator";
import MultiStepSignupDemo from "./pages/MultiStepSignupDemo";
import MultiStepSignupWithRoleDemo from "./pages/MultiStepSignupWithRoleDemo";
import EmailSubscription from './pages/EmailSubscription';
import FAQ from './pages/FAQ';
import UserDashboard from './pages/UserDashboard';
import GuestDashboard from './pages/GuestDashboard';
import UserProfile from './pages/UserProfile';
import MetricsDashboard from './pages/MetricsDashboard';
import DashboardsLandingPage from './pages/DashboardsLandingPage';
import DashboardRedirector from './pages/DashboardRedirector';
import ThankYou from "./pages/ThankYou";
import StatisticsPage from './pages/StatisticsPage';
import DipsHistoryPage from './pages/DipsHistoryPage';
import ProgressPage from './pages/ProgressPage';
import FavouritesPage from './pages/FavouritesPage';

import LocationDetailPage from './pages/LocationDetailPage';
import LogDipPage from './pages/LogDipPage';
import StatusPage from './pages/StatusPage';
import SaunaDetailsNordic from "./pages/SaunaDetailsNordic";
import Changelog from './pages/Changelog';
import OperatorPortal from "./pages/OperatorPortal";
import OperatorPreview from "./pages/OperatorPreview";
import ClaimBusiness from "./pages/ClaimBusiness";
import LoadingTestPage from "./pages/LoadingTestPage";
import OperatorAnalytics from './pages/OperatorAnalytics';

// Import Mixpanel
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import metricsService from './services/metricsService';
import { ErrorBoundary } from '@/components/error-boundary';
import { performanceMonitor } from '@/utils/performance';
import { FullPageLoading } from '@/components/ui/loading-spinner';

interface AppProps {
  baseUrl?: string;
}

const getLayout = (element: React.ReactNode) => <Layout>{element}</Layout>;

// Define a function to check for maintenance mode
const isMaintenanceMode = () => import.meta.env.VITE_MAINTENANCE_MODE === 'true';

const getAdminLayout = (element: React.ReactNode) => (
  <AdminRoute>
    <AdminLayout>{element}</AdminLayout>
  </AdminRoute>
)

const routerConfig = (baseUrl: string) => createBrowserRouter([
  {
    path: "/",
    element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Index />),
    errorElement: getLayout(<ErrorPage />),
  },
  { path: "/operator-survey", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Landing />), errorElement: getLayout(<ErrorPage />) },
  { path: "/coming-soon", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ComingSoon />), errorElement: getLayout(<ErrorPage />) },
  { path: "/social-assets", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SocialAssets />), errorElement: getLayout(<ErrorPage />) },
  { path: "/design-system", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<DesignSystem />), errorElement: getLayout(<ErrorPage />) },
  { path: "/dev-system", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<DevSystem />), errorElement: getLayout(<ErrorPage />) },
  { path: "/admin", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getAdminLayout(<DashboardsLandingPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/admin-landing", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute><AdminLanding /></ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  {
    path: '/admin/dashboards',
    element: getAdminLayout(<DashboardsLandingPage />),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/metrics',
    element: getAdminLayout(<MetricsDashboard />),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/dashboards/user-analytics',
    element: getAdminLayout(<div>User Analytics Dashboard</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/dashboards/sales',
    element: getAdminLayout(<div>Sales Dashboard</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  { path: "/admin/blogs", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getAdminLayout(<BlogIndexPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/admin/blogs/new", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getAdminLayout(<BlogFormPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/admin/blogs/edit/:id", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getAdminLayout(<BlogFormPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/login", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Login />), errorElement: getLayout(<ErrorPage />) },
  { path: "/forgot-password", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ForgotPassword />), errorElement: getLayout(<ErrorPage />) },
  { path: "/signup", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Signup />), errorElement: getLayout(<ErrorPage />) },
  { path: "/pricing", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<PricingPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/subscription", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Subscription />), errorElement: getLayout(<ErrorPage />) },
  { path: "/subscription-success", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SubscriptionSuccess />), errorElement: getLayout(<ErrorPage />) },
  { path: "/contact", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Contact />), errorElement: getLayout(<ErrorPage />) },
  { path: "/contact-thank-you", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ContactThankYou />), errorElement: getLayout(<ErrorPage />) },
  { path: "/sitemap", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Sitemap />), errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-onboarding", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<OperatorOnboarding />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/directory", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Directory />), errorElement: getLayout(<ErrorPage />) },
  { path: "/sauna/:id", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SaunaDetailsNordic />), errorElement: getLayout(<ErrorPage />) },
  { path: "/sauna-old/:id", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SaunaDetailsNordic />), errorElement: getLayout(<ErrorPage />) },
  { path: "/project", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ProjectIntro />), errorElement: getLayout(<ErrorPage />) },
  { path: "/about", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ProjectIntro />), errorElement: getLayout(<ErrorPage />) },
  { path: "/blog", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Blog />), errorElement: getLayout(<ErrorPage />) },
  { path: "/blog/category/:categorySlug", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Blog />), errorElement: getLayout(<ErrorPage />) },
  { path: "/blog/:slug", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<BlogPost />), errorElement: getLayout(<ErrorPage />) },
  { path: "/admin/operator-admin-simple", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<OperatorAdminSimple />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-admin-simple", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<OperatorAdminSimple />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-admin", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<OperatorAdmin />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/verify-email", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <VerifyEmail />, errorElement: getLayout(<ErrorPage />) },
  { path: "/terms-and-conditions", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<TermsAndConditions />), errorElement: getLayout(<ErrorPage />) },
  { path: "/privacy-policy", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<PrivacyPolicy />), errorElement: getLayout(<ErrorPage />) },
  { path: "/cookie-policy", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<CookiePolicy />), errorElement: getLayout(<ErrorPage />) },
  { path: "/acceptable-use-policy", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<AcceptableUsePolicy />), errorElement: getLayout(<ErrorPage />) },
  { path: "/terms-of-use", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<TermsOfUse />), errorElement: getLayout(<ErrorPage />) },
  { path: "/why-kit", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<WhyKit />), errorElement: getLayout(<ErrorPage />) },
  { path: `/we-need-your-help`, element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<WeNeedYourHelp />), errorElement: getLayout(<ErrorPage />) },
  { path: "/your-help", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<YourHelp />), errorElement: getLayout(<ErrorPage />) },
  { path: "/how-it-works", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<HowItWorks />), errorElement: getLayout(<ErrorPage />) },
  {
    path: "/maintenance",
    element: <MaintenancePage />,
    errorElement: getLayout(<ErrorPage />),
  },
  {
    path: '/admin/users',
    element: getAdminLayout(<UsersPage />),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/operators',
    element: getAdminLayout(<OperatorsPage />),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/locations',
    element: getAdminLayout(<LocationsPage />),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/leads',
    element: getAdminLayout(<LeadsPage />),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/services',
    element: getAdminLayout(<div>Services Management</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/memberships',
    element: getAdminLayout(<div>Memberships Management</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/bookings',
    element: getAdminLayout(<div>Bookings Management</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/reviews',
    element: getAdminLayout(<div>Reviews Management</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/blog',
    element: getAdminLayout(<div>Blog Management</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/support',
    element: getAdminLayout(<div>Support Management</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  {
    path: '/admin/settings',
    element: getAdminLayout(<div>Settings</div>),
    errorElement: getAdminLayout(<ErrorPage />),
  },
  { path: "/sauna-wizard", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SaunaWizardPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/tempsignup", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<TempSignupPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/TempComingSoonLandingPage", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<TempComingSoonLandingPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/TempMarketingLandingPage", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<TempMarketingLandingPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/help-book", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SaunaDipHelpBook />), errorElement: getLayout(<ErrorPage />) },
  { path: "/content-review", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ContentReviewPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/reset-password", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ResetPassword />), errorElement: getLayout(<ErrorPage />) },
  { path: "/choose-signup", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ChooseSignup />), errorElement: getLayout(<ErrorPage />) },
  { path: "/signup/operator", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SignupOperator />), errorElement: getLayout(<ErrorPage />) },
  { path: "/signup/guest", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<SignupGuest />), errorElement: getLayout(<ErrorPage />) },
  { path: "/thank-you", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ThankYou />), errorElement: getLayout(<ErrorPage />) },
  { path: "/multi-step-signup-demo", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<MultiStepSignupDemo />), errorElement: getLayout(<ErrorPage />) },
  { path: "/multi-step-signup-with-role-demo", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<MultiStepSignupWithRoleDemo />), errorElement: getLayout(<ErrorPage />) },
  { path: "/mailing-list/:type", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<EmailSubscription />), errorElement: getLayout(<ErrorPage />) },
  { path: "/faq", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<FAQ />), errorElement: getLayout(<ErrorPage />) },
  { path: "/dashboard", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<DashboardRedirector />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/user-dashboard", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<UserDashboard />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/guest-dashboard", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<GuestDashboard />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/statistics", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<StatisticsPage />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/dips", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<DipsHistoryPage />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/progress", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<ProgressPage />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/favourites", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<FavouritesPage />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  
  { path: "/locations/:id", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<LocationDetailPage />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/log", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<LogDipPage />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/profile", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<UserProfile />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/settings", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : <ProtectedRoute>{getLayout(<UserProfile />)}</ProtectedRoute>, errorElement: getLayout(<ErrorPage />) },
  { path: "/status", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<StatusPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/system-status", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<StatusPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/sauna-details-nordic", element: getLayout(<SaunaDetailsNordic />), errorElement: getLayout(<ErrorPage />) },
  { path: "/changelog", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<Changelog />), errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-portal", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<OperatorPortal />), errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-preview/:token", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<OperatorPreview />), errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-preview-mock", element: <OperatorPreview mock={true} /> },
  { path: "/claim-business", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<ClaimBusiness />), errorElement: getLayout(<ErrorPage />) },
  { path: "/become-operator", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<OperatorOnboarding />), errorElement: getLayout(<ErrorPage />) },
  { path: "/loading-test", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<LoadingTestPage />), errorElement: getLayout(<ErrorPage />) },
  { path: "/operator-analytics", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<OperatorAnalytics />), errorElement: getLayout(<ErrorPage />) },
  { path: "*", element: isMaintenanceMode() ? <Navigate to="/maintenance" /> : getLayout(<NotFound />), errorElement: getLayout(<ErrorPage />) },
], {
  basename: baseUrl
});

const App = ({ baseUrl = '/' }: AppProps) => {
  const router = routerConfig(baseUrl);
  const isProd = import.meta.env.MODE === 'production';
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize performance monitoring
    performanceMonitor.measurePerformance('App Initialization', () => {
      console.log('App initialized with performance monitoring')
    })
    
    const startTime = performance.now();
    
    const trackPageLoad = () => {
      const loadTime = performance.now() - startTime;
      metricsService.trackPageLoad(window.location.pathname, loadTime);
    };
  
    window.addEventListener('load', trackPageLoad);
    return () => window.removeEventListener('load', trackPageLoad);
  }, []);

  // Simple loading effect for testing
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Developer-only toggle: force Coming Soon page if ?comingsoon=1 is in the URL
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const forceComingSoon = searchParams?.get('comingsoon') === '1';

  // Initialize Mixpanel only in production
  useEffect(() => {
    if (isProd && import.meta.env.VITE_MIXPANEL_TOKEN) {
      mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, { debug: false, ignore_dnt: true });
      console.log('Mixpanel initialized in production');
      mixpanel.track('App Initialized');
    }
  }, [isProd]);

  // Consider identifying users after login using mixpanel.identify(userId) 
  // and setting user properties with mixpanel.people.set({ ... });

  if (forceComingSoon) {
    // Render Coming Soon page inside the normal layout
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <AuthProvider>
              <ThemeProvider>
                <TooltipProvider>
                  <CookieConsent
                    location="bottom"
                    buttonText="Accept"
                    cookieName="saunadip-cookie-consent"
                    style={{ background: "#0C3535", color: "white" }}
                    buttonStyle={{ background: "#F5542D", color: "white", borderRadius: "4px", padding: "0.5rem 1rem" }}
                  >
                    This website uses cookies to enhance the user experience. By continuing to browse, you agree to our use of cookies. For more details, please see our <a href="/cookie-policy" style={{ color: "#F5542D" }}>Cookie Policy</a>.
                  </CookieConsent>
                  {getLayout(<ComingSoon />)}
                  <Toaster />
                  <Sonner />
                </TooltipProvider>
              </ThemeProvider>
            </AuthProvider>
          </HelmetProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  if (isLoading) {
    return <FullPageLoading message="Loading SaunaDip..." />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* Add HelmetProvider here to manage tags in the document head */}
        <HelmetProvider>

        {/* Add Microsoft Clarity and Plausible scripts using Helmet */} 
        <Helmet>
          {/* Microsoft Clarity - only in production */}
          {isProd && import.meta.env.VITE_CLARITY_ID && (
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?ref=bwt";y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${import.meta.env.VITE_CLARITY_ID}");
                `,
              }}
            ></script>
          )}

          {/* Plausible Analytics - only in production */}
          {isProd && import.meta.env.VITE_PLAUSIBLE_DOMAIN && (
            <>
              <script
                defer
                data-domain={import.meta.env.VITE_PLAUSIBLE_DOMAIN}
                src="https://plausible.io/js/script.file-downloads.hash.outbound-links.pageview-props.revenue.tagged-events.js"
              ></script>
              <script dangerouslySetInnerHTML={{
                __html: `
                  window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }
                `
              }} />
            </>
          )}

          {/* Google Analytics - only in production */}
          {isProd && (
            <>
              <script async src="https://www.googletagmanager.com/gtag/js?id=G-8SKFT95T54"></script>
              <script dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', 'G-8SKFT95T54');
                `
              }} />
            </>
          )}
        </Helmet>

        <AuthProvider>
          <ThemeProvider>
            <TooltipProvider>
              <CookieConsent
                location="bottom"
                buttonText="Accept"
                cookieName="saunadip-cookie-consent"
                style={{ background: "#0C3535", color: "white" }}
                buttonStyle={{ background: "#F5542D", color: "white", borderRadius: "4px", padding: "0.5rem 1rem" }}
              >
                This website uses cookies to enhance the user experience. By continuing to browse, you agree to our use of cookies. For more details, please see our <a href="/cookie-policy" style={{ color: "#F5542D" }}>Cookie Policy</a>.
              </CookieConsent>
              {/* Conditionally render MaintenancePage or RouterProvider */}
              {isMaintenanceMode() ? <MaintenancePage /> : (
                <RouterProvider router={router} />
              )}
              <Toaster />
              <Sonner />
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
              </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;